import React, { useCallback, useEffect, useState } from "react";
import api, { extractErrorMessage } from "@/lib/api";
import { useLang } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Wallet, Plus, Pencil, Trash2, Receipt } from "lucide-react";

/**
 * A20-P3 — Budget Engine panel rendered inside the event drawer.
 *
 * Props:
 *   eventId: required — the parent calendar event id.
 *   onChange: optional — called when the budget state changes (so the
 *             parent list can refresh totals if it ever shows them).
 *
 * Self-contained: handles its own data fetching against
 * `/api/calendar/events/{event_id}/budget(/lines)`.
 *
 * Per A20-P0.1 P4 decision: EUR only.
 */

const CATEGORIES = [
  "flights", "hotels", "transfers", "car_rental", "fuel", "taxi_uber",
  "meals", "dinners", "per_diem",
  "event_tickets", "venue_access", "accreditation", "venue_rental",
  "promotional_material", "gifts_presents", "merchandising",
  "contingency_unexpected", "insurance", "visa_documents", "luggage",
  "technology", "audiovisual", "staff", "translators", "photo_video",
  "other",
];
const BUDGET_STATUSES = ["draft", "planned", "approved", "closed"];
const LINE_STATUSES = ["planned", "committed", "paid", "cancelled"];

const fmtEur = (n) => {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency", currency: "EUR", maximumFractionDigits: 2,
    }).format(Number(n) || 0);
  } catch { return `€${(Number(n) || 0).toFixed(2)}`; }
};

const emptyLine = () => ({
  category: "flights",
  description: "",
  estimated_amount: "",
  actual_amount: "",
  vendor: "",
  date: "",
  status: "planned",
});

export const BudgetPanel = ({ eventId, onChange }) => {
  const { t } = useLang();
  const [budget, setBudget] = useState(null);
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showHeaderForm, setShowHeaderForm] = useState(false);
  const [headerForm, setHeaderForm] = useState({
    title: "", description: "", status: "draft",
  });

  const [editingLineId, setEditingLineId] = useState(null);
  const [showLineForm, setShowLineForm] = useState(false);
  const [lineForm, setLineForm] = useState(emptyLine());

  const fetchAll = useCallback(async () => {
    if (!eventId) return;
    setLoading(true);
    try {
      const [bRes, lRes] = await Promise.all([
        api.get(`/calendar/events/${eventId}/budget`),
        api.get(`/calendar/events/${eventId}/budget/lines`).catch(() => ({ data: { lines: [] } })),
      ]);
      setBudget(bRes.data?.budget || null);
      setLines(lRes.data?.lines || []);
    } catch (err) {
      // Non-fatal: keep silent on 404 (no budget yet).
      if (err?.response?.status !== 404) {
        toast.error(extractErrorMessage(err, "load_failed"));
      }
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openCreateHeader = () => {
    setHeaderForm({ title: "", description: "", status: "draft" });
    setShowHeaderForm(true);
  };
  const openEditHeader = () => {
    setHeaderForm({
      title: budget?.title || "",
      description: budget?.description || "",
      status: budget?.status || "draft",
    });
    setShowHeaderForm(true);
  };
  const submitHeader = async () => {
    const payload = {
      title: headerForm.title.trim(),
      description: headerForm.description.trim() || null,
      status: headerForm.status,
    };
    try {
      if (budget) {
        await api.put(`/calendar/events/${eventId}/budget`, payload);
        toast.success(t("budget.toasts.updated"));
      } else {
        await api.post(`/calendar/events/${eventId}/budget`, payload);
        toast.success(t("budget.toasts.created"));
      }
      setShowHeaderForm(false);
      await fetchAll();
      onChange?.();
    } catch (err) {
      toast.error(extractErrorMessage(err, t("budget.toasts.saveFailed")));
    }
  };
  const deleteBudget = async () => {
    if (!window.confirm(t("budget.confirmDelete"))) return;
    try {
      await api.delete(`/calendar/events/${eventId}/budget`);
      toast.success(t("budget.toasts.deleted"));
      setBudget(null);
      setLines([]);
      onChange?.();
    } catch (err) {
      toast.error(extractErrorMessage(err, t("budget.toasts.saveFailed")));
    }
  };

  const openCreateLine = () => {
    setEditingLineId(null);
    setLineForm(emptyLine());
    setShowLineForm(true);
  };
  const openEditLine = (ln) => {
    setEditingLineId(ln.id);
    setLineForm({
      category: ln.category,
      description: ln.description || "",
      estimated_amount: ln.estimated_amount ?? "",
      actual_amount: ln.actual_amount ?? "",
      vendor: ln.vendor || "",
      date: ln.date || "",
      status: ln.status,
    });
    setShowLineForm(true);
  };
  const submitLine = async () => {
    const payload = {
      category: lineForm.category,
      description: lineForm.description.trim() || null,
      estimated_amount: Number(lineForm.estimated_amount) || 0,
      actual_amount: Number(lineForm.actual_amount) || 0,
      vendor: lineForm.vendor.trim() || null,
      date: lineForm.date || null,
      status: lineForm.status,
    };
    try {
      if (editingLineId) {
        await api.put(`/calendar/events/${eventId}/budget/lines/${editingLineId}`, payload);
        toast.success(t("budget.toasts.lineUpdated"));
      } else {
        await api.post(`/calendar/events/${eventId}/budget/lines`, payload);
        toast.success(t("budget.toasts.lineCreated"));
      }
      setShowLineForm(false);
      await fetchAll();
      onChange?.();
    } catch (err) {
      toast.error(extractErrorMessage(err, t("budget.toasts.saveFailed")));
    }
  };
  const deleteLine = async (ln) => {
    if (!window.confirm(t("budget.line.confirmDeleteLine"))) return;
    try {
      await api.delete(`/calendar/events/${eventId}/budget/lines/${ln.id}`);
      toast.success(t("budget.toasts.lineDeleted"));
      await fetchAll();
      onChange?.();
    } catch (err) {
      toast.error(extractErrorMessage(err, t("budget.toasts.saveFailed")));
    }
  };

  // ---------- Render ----------
  if (!eventId) return null;

  const variance = Number(budget?.variance_amount || 0);
  const variancePct = Number(budget?.variance_percentage || 0);
  const overBudget = variance > 0;
  const underBudget = variance < 0;

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 space-y-3" data-testid="budget-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Wallet className="w-4 h-4 text-emerald-700" />
          <span className="text-sm font-semibold text-slate-800">
            {t("budget.sectionTitle")}
          </span>
          {budget && (
            <Badge className="bg-slate-200 text-slate-800 border-0 text-[10px] uppercase tracking-wide">
              {t(`budget.status.${budget.status}`) || budget.status}
            </Badge>
          )}
        </div>
        {budget ? (
          <div className="flex items-center gap-1">
            <Button size="sm" variant="ghost" onClick={openEditHeader} data-testid="budget-edit-btn">
              <Pencil className="w-3.5 h-3.5" />
            </Button>
            <Button size="sm" variant="ghost" onClick={deleteBudget} data-testid="budget-delete-btn">
              <Trash2 className="w-3.5 h-3.5 text-rose-600" />
            </Button>
          </div>
        ) : !showHeaderForm && (
          <Button
            size="sm"
            onClick={openCreateHeader}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="budget-create-btn"
          >
            <Plus className="w-3.5 h-3.5 me-1" />
            {t("budget.create")}
          </Button>
        )}
      </div>

      <p className="text-[10px] text-slate-400">{t("budget.privacy")}</p>

      {/* Empty state */}
      {!budget && !showHeaderForm && (
        <p className="text-xs text-slate-500" data-testid="budget-empty">
          {t("budget.none")}
        </p>
      )}

      {/* Header form (create or edit) */}
      {showHeaderForm && (
        <div className="space-y-2 bg-white border rounded p-3" data-testid="budget-header-form">
          <label className="text-xs block">
            <span className="block text-slate-600 mb-1">{t("budget.form.titleLabel")} *</span>
            <Input
              value={headerForm.title}
              onChange={(e) => setHeaderForm({ ...headerForm, title: e.target.value })}
              maxLength={200}
              data-testid="budget-header-title"
            />
          </label>
          <label className="text-xs block">
            <span className="block text-slate-600 mb-1">{t("budget.form.descriptionLabel")}</span>
            <Textarea
              rows={2}
              value={headerForm.description}
              onChange={(e) => setHeaderForm({ ...headerForm, description: e.target.value })}
              maxLength={2000}
              data-testid="budget-header-description"
            />
          </label>
          <label className="text-xs block">
            <span className="block text-slate-600 mb-1">{t("budget.form.statusLabel")}</span>
            <select
              className="w-full border rounded px-2 py-1.5 text-xs"
              value={headerForm.status}
              onChange={(e) => setHeaderForm({ ...headerForm, status: e.target.value })}
              data-testid="budget-header-status"
            >
              {BUDGET_STATUSES.map((s) => (
                <option key={s} value={s}>{t(`budget.status.${s}`) || s}</option>
              ))}
            </select>
          </label>
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="outline" onClick={() => setShowHeaderForm(false)} data-testid="budget-header-cancel">
              {t("budget.cancel")}
            </Button>
            <Button
              size="sm"
              onClick={submitHeader}
              disabled={!headerForm.title.trim()}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="budget-header-save"
            >
              {t("budget.save")}
            </Button>
          </div>
        </div>
      )}

      {/* Totals strip */}
      {budget && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs" data-testid="budget-totals">
          <div className="bg-white border rounded p-2">
            <div className="text-slate-500 text-[10px] uppercase tracking-wide">{t("budget.totals.estimated")}</div>
            <div className="font-semibold tabular-nums" data-testid="budget-total-estimated">{fmtEur(budget.estimated_total)}</div>
          </div>
          <div className="bg-white border rounded p-2">
            <div className="text-slate-500 text-[10px] uppercase tracking-wide">{t("budget.totals.actual")}</div>
            <div className="font-semibold tabular-nums" data-testid="budget-total-actual">{fmtEur(budget.actual_total)}</div>
          </div>
          <div className={`border rounded p-2 ${overBudget ? "bg-rose-50 border-rose-200" : underBudget ? "bg-emerald-50 border-emerald-200" : "bg-white"}`}>
            <div className="text-slate-500 text-[10px] uppercase tracking-wide">{t("budget.totals.variance")}</div>
            <div className={`font-semibold tabular-nums ${overBudget ? "text-rose-700" : underBudget ? "text-emerald-700" : "text-slate-900"}`} data-testid="budget-total-variance">
              {variance > 0 ? "+" : ""}{fmtEur(variance)}
              {budget.estimated_total > 0 && (
                <span className="text-[10px] text-slate-500 ms-1">
                  ({variancePct > 0 ? "+" : ""}{variancePct.toFixed(1)}%)
                </span>
              )}
            </div>
          </div>
          <div className="bg-white border rounded p-2">
            <div className="text-slate-500 text-[10px] uppercase tracking-wide">{t("budget.totals.lineCount")}</div>
            <div className="font-semibold tabular-nums" data-testid="budget-line-count">{budget.line_count || 0}</div>
          </div>
        </div>
      )}

      {/* Lines list */}
      {budget && (
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-slate-700 inline-flex items-center gap-1">
              <Receipt className="w-3.5 h-3.5" />
              {t("budget.totals.lineCount")}
            </span>
            {!showLineForm && (
              <Button size="sm" variant="outline" onClick={openCreateLine} data-testid="budget-line-add-btn">
                <Plus className="w-3.5 h-3.5 me-1" />
                {t("budget.line.addBtn")}
              </Button>
            )}
          </div>

          {showLineForm && (
            <div className="bg-white border rounded p-3 space-y-2" data-testid="budget-line-form">
              <div className="grid sm:grid-cols-2 gap-2">
                <label className="text-xs">
                  <span className="block text-slate-600 mb-1">{t("budget.line.categoryLabel")}</span>
                  <select
                    className="w-full border rounded px-2 py-1.5 text-xs"
                    value={lineForm.category}
                    onChange={(e) => setLineForm({ ...lineForm, category: e.target.value })}
                    data-testid="budget-line-form-category"
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c} value={c}>{t(`budget.categories.${c}`) || c}</option>
                    ))}
                  </select>
                </label>
                <label className="text-xs">
                  <span className="block text-slate-600 mb-1">{t("budget.line.statusLabel")}</span>
                  <select
                    className="w-full border rounded px-2 py-1.5 text-xs"
                    value={lineForm.status}
                    onChange={(e) => setLineForm({ ...lineForm, status: e.target.value })}
                    data-testid="budget-line-form-status"
                  >
                    {LINE_STATUSES.map((s) => (
                      <option key={s} value={s}>{t(`budget.lineStatus.${s}`) || s}</option>
                    ))}
                  </select>
                </label>
              </div>
              <label className="text-xs block">
                <span className="block text-slate-600 mb-1">{t("budget.line.descLabel")}</span>
                <Input
                  value={lineForm.description}
                  onChange={(e) => setLineForm({ ...lineForm, description: e.target.value })}
                  maxLength={2000}
                  data-testid="budget-line-form-description"
                />
              </label>
              <div className="grid sm:grid-cols-2 gap-2">
                <label className="text-xs">
                  <span className="block text-slate-600 mb-1">{t("budget.line.estimatedLabel")}</span>
                  <Input
                    type="number" min="0" step="0.01"
                    value={lineForm.estimated_amount}
                    onChange={(e) => setLineForm({ ...lineForm, estimated_amount: e.target.value })}
                    data-testid="budget-line-form-estimated"
                  />
                </label>
                <label className="text-xs">
                  <span className="block text-slate-600 mb-1">{t("budget.line.actualLabel")}</span>
                  <Input
                    type="number" min="0" step="0.01"
                    value={lineForm.actual_amount}
                    onChange={(e) => setLineForm({ ...lineForm, actual_amount: e.target.value })}
                    data-testid="budget-line-form-actual"
                  />
                </label>
              </div>
              <div className="grid sm:grid-cols-2 gap-2">
                <label className="text-xs">
                  <span className="block text-slate-600 mb-1">{t("budget.line.vendorLabel")}</span>
                  <Input
                    value={lineForm.vendor}
                    onChange={(e) => setLineForm({ ...lineForm, vendor: e.target.value })}
                    maxLength={200}
                    data-testid="budget-line-form-vendor"
                  />
                </label>
                <label className="text-xs">
                  <span className="block text-slate-600 mb-1">{t("budget.line.dateLabel")}</span>
                  <Input
                    type="date"
                    value={lineForm.date}
                    onChange={(e) => setLineForm({ ...lineForm, date: e.target.value })}
                    data-testid="budget-line-form-date"
                  />
                </label>
              </div>
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="outline" onClick={() => setShowLineForm(false)} data-testid="budget-line-form-cancel">
                  {t("budget.cancel")}
                </Button>
                <Button
                  size="sm"
                  onClick={submitLine}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="budget-line-form-save"
                >
                  {t("budget.save")}
                </Button>
              </div>
            </div>
          )}

          {lines.length === 0 && !showLineForm ? (
            <p className="text-xs text-slate-500 py-2" data-testid="budget-lines-empty">
              {t("budget.line.empty")}
            </p>
          ) : (
            <ul className="space-y-1.5">
              {lines.map((ln) => (
                <li
                  key={ln.id}
                  className="bg-white border rounded p-2 flex items-start gap-2 text-xs"
                  data-testid={`budget-line-row-${ln.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge className="bg-slate-100 text-slate-800 border-0 text-[10px]">
                        {t(`budget.categories.${ln.category}`) || ln.category}
                      </Badge>
                      <Badge className="bg-emerald-100 text-emerald-900 border-0 text-[10px]">
                        {t(`budget.lineStatus.${ln.status}`) || ln.status}
                      </Badge>
                      {ln.vendor && (
                        <span className="text-slate-500">· {ln.vendor}</span>
                      )}
                      {ln.date && <span className="text-slate-400">· {ln.date}</span>}
                    </div>
                    {ln.description && (
                      <div className="text-slate-700 mt-0.5 truncate">{ln.description}</div>
                    )}
                    <div className="text-slate-600 mt-0.5 tabular-nums">
                      <span>{t("budget.totals.estimated")}: <strong>{fmtEur(ln.estimated_amount)}</strong></span>
                      <span className="ms-3">{t("budget.totals.actual")}: <strong>{fmtEur(ln.actual_amount)}</strong></span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button size="sm" variant="ghost" onClick={() => openEditLine(ln)} data-testid={`budget-line-edit-${ln.id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => deleteLine(ln)} data-testid={`budget-line-delete-${ln.id}`}>
                      <Trash2 className="w-3.5 h-3.5 text-rose-600" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default BudgetPanel;
